import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getGiftcardsPaginatedApi,
  getGiftcardApi,
  createGiftcardApi,
  updateGiftcardApi,
  deleteGiftcardApi,
  importGiftcardsApi,
  type ImportGiftcardsParams,
} from "../api/giftcardsApi";
import {
  catalogQueryKey,
  storeScope,
  type CatalogScope,
} from "../api/catalogScope";
import type { StoreId, GiftCardId } from "@KassaroAB/shared/types/ids";
import type { RestCreateGiftCard, RestUpdateGiftCard } from "@KassaroAB/shared/types/giftCard";

const giftCardsQueryKey = (scope: CatalogScope | null) =>
  catalogQueryKey(scope, "giftCards");

export const useGetGiftcardsInfiniteQuery = (scope: CatalogScope | null) => {
  return useInfiniteQuery({
    queryKey: [...giftCardsQueryKey(scope), "infinite"],
    queryFn: ({ pageParam }: { pageParam: GiftCardId | null }) =>
      getGiftcardsPaginatedApi(scope!, pageParam, 20),
    initialPageParam: null,
    getNextPageParam: (lastPage) => {
      if (lastPage.length < 20) return undefined;
      return lastPage.length > 0 ? lastPage[lastPage.length - 1].id : undefined;
    },
    enabled: !!scope,
  });
};

export const useGetGiftcardQuery = (
  storeId: StoreId | null,
  giftcardId: GiftCardId | null,
) => {
  return useQuery({
    queryKey: ["stores", storeId, "giftcards", giftcardId],
    queryFn: () => getGiftcardApi(storeId!, giftcardId!),
    enabled: !!storeId && !!giftcardId,
  });
};

export const useCreateGiftcardMutation = (storeId: StoreId | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (giftcard: RestCreateGiftCard) =>
      createGiftcardApi(storeId!, giftcard),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: giftCardsQueryKey(storeId ? storeScope(storeId) : null),
      });
    },
  });
};

export const useUpdateGiftcardMutation = (storeId: StoreId | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      giftcardId,
      giftcard,
    }: {
      giftcardId: GiftCardId;
      giftcard: RestUpdateGiftCard;
    }) => updateGiftcardApi(storeId!, giftcardId, giftcard),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: giftCardsQueryKey(storeId ? storeScope(storeId) : null),
      });
    },
  });
};

export const useDeleteGiftcardMutation = (storeId: StoreId | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (giftcardId: GiftCardId) =>
      deleteGiftcardApi(storeId!, giftcardId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: giftCardsQueryKey(storeId ? storeScope(storeId) : null),
      });
    },
  });
};

export const useImportGiftcardsMutation = (storeId: StoreId | null) => {
  return useMutation({
    mutationFn: (params: ImportGiftcardsParams) => {
      if (!storeId) {
        throw new Error("Store ID is required");
      }
      return importGiftcardsApi(storeId, params);
    },
  });
};
